/**
 * @Description  :
 * This React component `ImportVendors` provides a UI for importing vendors (Suppliers or Contractors) via Excel file upload.
 * 
 * Core functionalities include:
 *  - Downloading a pre-defined import template for vendors.
 *  - Uploading and parsing Excel (.xlsx) files.
 *  - Dynamically mapping Excel columns to expected vendor fields.
 *  - Validating each vendor entry using the defined AJV schema (`VendorSchema`), including:
 *      • Checking for required fields and valid data formats.
 *      • Preventing duplicate ABN-Business Name combinations against existing vendors.
 *      • Preventing more than one primary contact per vendor.
 *      • Detecting duplicate entries within the uploaded file.
 *  - Displaying a validation summary (valid, invalid, total).
 *  - Allowing full import or "Ignore and Import" based on validation.
 *  - Exporting the validated data (with error details) for offline editing.
 *
 * This component handles both Suppliers and Contractors, determined by the route.
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import FileUploader from '../../components/file-uploader';
import SelectInput from '../../components/inputs/select';
import CustomTable from '../../components/table/table';
import { CONTRACTORS, CONTRACTORS_LIST, SUPPLIERS, SUPPLIERS_LIST, VENDOR, VENDOR_LIST } from '../../constants/route-paths';
import { addBulkVendor, getVendorList } from '../../actions/vendor-management.action';
import { allowedExcelTypes } from '../../util/validators/file';
import { VendorSchema } from '../../util/validators/vendorSchema';
import Ajv from 'ajv';
import AjvErrors from 'ajv-errors';
import _ from 'lodash';
import CheckIcon from '@mui/icons-material/Check';
import idx from 'idx';
import { warningSnackBar } from '../../actions/common.action';
import { VENDOR_IMPORT_TYPE, VENDOR_URL_PARAM, VENDOR_TYPE_LABEL, VENDOR_STATUS } from '../../constants/vendor.contant';
/**
 * @Modified     : CTBL-333: Fixed validation issues in supplier and contractor bulk import process
 *
 * @Description  :
 * This React component `ImportVendors` provides a UI for importing vendors (Suppliers or Contractors) via Excel file upload.
 * 
 * Core functionalities include:
 *  - Downloading a pre-defined import template for vendors.
 *  - Uploading and parsing Excel (.xlsx) files.
 *  - Dynamically mapping Excel columns to expected vendor fields.
 *  - Validating each vendor entry using the defined AJV schema (`VendorSchema`), including:
 *      • Checking for required fields and valid data formats.
 *      • Preventing duplicate ABN-Business Name combinations against existing vendors.
 *      • Preventing more than one primary contact per vendor.
 *      • Detecting duplicate entries within the uploaded file.
 *  - Displaying a validation summary (valid, invalid, total).
 *  - Allowing full import or "Ignore and Import" based on validation.
 *  - Exporting the validated data (with error details) for offline editing.
 *
 * This component handles both Suppliers and Contractors, determined by the route.
 */

const ajv = new Ajv({ allErrors: true });
AjvErrors(ajv, { singleError: true });

const businessName = 'Business Name';
const abn = 'Enter 11 digit ABN';
const streetAddress = 'Street Address';
const suburb = 'Suburb';
const state = 'State';
const postcode = 'Postcode';
const country = 'Country';
const phoneNumber = 'Phone Number';
const email = 'Email';
const paymentTerms = 'Payment Terms';
const contactGivenName = 'Contact Given Name';
const contactLastName = 'Contact Last Name';
const contactEmail = 'Contact Email';
const contactPhoneNumber = 'Contact Phone Number';
const contactJobRole = 'Contact Job Role';
const setPrimaryContact = 'Set Primary Contact';
const website = 'Website';

const defaults = {
    [businessName]: { key: businessName, label: businessName, items: [] },
    [abn]: { key: abn, label: abn, items: [] },
    [streetAddress]: { key: streetAddress, label: streetAddress, items: [] },
    [suburb]: { key: suburb, label: suburb, items: [] },
    [state]: { key: state, label: state, items: [] },
    [postcode]: { key: postcode, label: postcode, items: [] },
    [country]: { key: country, label: country, items: [] },
    [phoneNumber]: { key: phoneNumber, label: phoneNumber, items: [] },
    [email]: { key: email, label: email, items: [] },
    [website]: { key: website, label: website, items: [] },
    [paymentTerms]: { key: paymentTerms, label: paymentTerms, items: [] },
    [contactGivenName]: { key: contactGivenName, label: contactGivenName, items: [] },
    [contactLastName]: { key: contactLastName, label: contactLastName, items: [] },
    [contactEmail]: { key: contactEmail, label: contactEmail, items: [] },
    [contactPhoneNumber]: { key: contactPhoneNumber, label: contactPhoneNumber, items: [] },
    [contactJobRole]: { key: contactJobRole, label: contactJobRole, items: [] },
    [setPrimaryContact]: { key: setPrimaryContact, label: setPrimaryContact, items: [] },
};

const validate = ajv.compile(VendorSchema);

function ImportVendors(props) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const pathName = idx(location, (_) => _.pathname);
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [mappers, setMappers] = useState(_.cloneDeep(defaults));
    const [status, setStatus] = useState({ total: 0, valid: 0, invalid: 0 });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [vendors, setVendors] = useState([]);
    let vendorType = '';
    let vendorTypeName = '';
    if (pathName) {
        // Handle both direct routes (/suppliers/, /contractors/) and vendor routes (/vendor/import-suppliers, /vendor/import-contractors)
        const pathSegments = pathName.split('/').filter(segment => segment); // Remove empty segments

        if (pathSegments[0] === SUPPLIERS) {
            vendorType = VENDOR_IMPORT_TYPE.SUPPLIER;
            vendorTypeName = VENDOR_TYPE_LABEL.SUPPLIERS;
        } else if (pathSegments[0] === CONTRACTORS) {
            vendorType = VENDOR_IMPORT_TYPE.SUB_CONTRACTOR;
            vendorTypeName = VENDOR_TYPE_LABEL.CONTRACTORS;
        } else if (pathSegments[0] === VENDOR) {
            // Handle vendor context routes
            if (pathSegments[1] === VENDOR_IMPORT_TYPE.SUPPLIERS) {
                vendorType = VENDOR_IMPORT_TYPE.SUPPLIER;
                vendorTypeName = VENDOR_TYPE_LABEL.SUPPLIERS;
            } else if (pathSegments[1] === VENDOR_IMPORT_TYPE.CONTRACTORS) {
                vendorType = VENDOR_IMPORT_TYPE.SUB_CONTRACTOR;
                vendorTypeName = VENDOR_TYPE_LABEL.CONTRACTORS;
            }
        }
    }

    useEffect(() => {
        dispatch(getVendorList()).then(res => { setVendors(res) }).catch(e => console.error(e));
    }, []);

    const cancelHandler = () => {
        // Get return tab parameters from URL
        const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB);
        const returnSubTab = searchParams.get(VENDOR_URL_PARAM.RETURN_SUB_TAB) || VENDOR_STATUS.ACTIVE;

        // Check if we're in vendor context
        if (pathName.includes(`/${VENDOR}/`)) {
            if (returnTab && returnSubTab) {
                navigate(`/${VENDOR}/${VENDOR_LIST}?${VENDOR_URL_PARAM.TAB}=${returnTab}&${VENDOR_URL_PARAM.SUB_TAB}=${returnSubTab}`);
            } else {
                // Default tab based on vendor type
                const defaultTab = vendorTypeName.toLowerCase() === SUPPLIERS ? SUPPLIERS : CONTRACTORS;
                navigate(`/${VENDOR}/${VENDOR_LIST}?${VENDOR_URL_PARAM.TAB}=${defaultTab}&${VENDOR_URL_PARAM.SUB_TAB}=${VENDOR_STATUS.ACTIVE}`);
            }
        } else {
            // Original behavior for direct supplier/contractor access
            if (vendorTypeName.toLowerCase() === SUPPLIERS.toLowerCase()) navigate(`/${SUPPLIERS}/${SUPPLIERS_LIST}`);
            else if (vendorTypeName.toLowerCase() === CONTRACTORS.toLowerCase()) navigate(`/${CONTRACTORS}/${CONTRACTORS_LIST}`);
        }
    };

    const downloadTemplate = () => {
        const template = [
            {
                [businessName]: '',
                [abn]: '',
                [streetAddress]: '',
                [suburb]: '',
                [state]: '',
                [postcode]: '',
                [country]: '',
                [phoneNumber]: '',
                [email]: '',
                [website]: '',
                [paymentTerms]: '',
                [contactGivenName]: '',
                [contactLastName]: '',
                [contactEmail]: '',
                [contactPhoneNumber]: '',
                [contactJobRole]: '',
                [setPrimaryContact]: '',
            },
        ];
        const filename = `${vendorTypeName}_Template.xlsx`;
        const ws = XLSX.utils.json_to_sheet(template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, filename);
    };

    const parseXlsx = (file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const workbook = XLSX.read(event.target.result, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const sheetData = XLSX.utils.sheet_to_json(sheet) || [];
            const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
            let _headers = headerRow || [];
            let _mappers = {};

            for (const key in defaults) {
                if (key !== 'error') {  // Ignore the 'error' column
                    _mappers[key] = {
                        ...defaults[key],
                        // key: _headers.includes(defaults[key].key) ? defaults[key].key : _headers.includes(`Generated ${defaults[key].key}`) ? `Generated ${defaults[key].key}` : '',
                        key: _headers.includes(defaults[key].key) ? defaults[key].key : '',
                        items: _headers.map((item) => ({ key: item, value: item })),
                    };
                }
            }

            if (isEmptyRow(sheetData)) {
                dispatch(warningSnackBar("Please Fill the data in Excel sheet!"));
                setMappers(_mappers);
                setData(sheetData);
                setStatus({ total: sheetData.length, valid: 0, invalid: 0 });
            }
            else {
                setMappers(_mappers);
                setData(sheetData);
                setStatus({ total: sheetData.length, valid: 0, invalid: 0 });
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const trimData = (data) => {
        return data ? data.toString().trim() : '';
    }

    const trimAndRemoveWhiteSpaces = (data) => {
        data = data.toString().trim();
        const isEmpty = data.length === 0;
        return isEmpty ? data.trim() : data.replace(/\s/g, '');
    }

    const removeSpecialCharacters = (data) => {
        return data ? data.toString().replace(/[^a-zA-Z0-9]/g, '') : data;
    }

    const getFormattedPhoneNumber = (data) => {
        if (data) return removeSpecialCharacters(String(data))
        else return data
    };

    const getFormattedWebsite = (data) => {
        if (data) return trimAndRemoveWhiteSpaces(data).split(',');
        else return [];
    };

    const getFormattedABN = (data) => {
        if (!data) return undefined;
        const cleaned = trimAndRemoveWhiteSpaces(String(data));
        return cleaned.length === 0 ? undefined : cleaned;
    };

    const getCleanedBusinessName = (data) => {
        data = trimData(data);
        if (data.length === 0) return data;
        return data;
    };

    const getCleanedStreetAddress = (data) => {
        data = trimData(data);
        if (data.length === 0) return data;
        return data;
    };

    const getCleanedState = (data) => {
        data = trimData(data);
        if (data.length === 0) return data;
        return data;
    };

    const getCleanedSuburb = (data) => {
        data = trimData(data);
        if (data.length === 0) return data;
        return data;
    };

    const getCleanedCountry = (data) => {
        data = trimData(data);
        if (data.length === 0) return data;
        return data;
    };

    const validateData = (isSubmit = false) => {
        let formatted = {};
        let _temp = [];
        let valid = false;
        let count = { total: status.total, valid: 0, invalid: 0 };
        let vendorMap = new Map();

        try {
            // 'vendors' is the existing list of vendors from state
            const existingVendorsMap = new Map(vendors.map(v => [`${v.businessName}-${v.abn}`, v]));
            const existingAbn = new Set(vendors.map(v => v.abn));

            let _data = data.map((item) => {
                const rawContact = {
                    firstName: item[mappers[contactGivenName].key],
                    lastName: item[mappers[contactLastName].key],
                    email: item[mappers[contactEmail].key],
                    phoneNumber: item[mappers[contactPhoneNumber].key],
                    designation: item[mappers[contactJobRole].key],
                    isPrimary: ['Yes'].includes((item[mappers[setPrimaryContact].key])),
                };

                const cleanedContact = {};
                Object.entries(rawContact).forEach(([key, value]) => {
                    if (typeof value === 'string') {
                        const trimmed = trimAndRemoveWhiteSpaces(value);
                        if (trimmed) cleanedContact[key] = trimmed;
                    } else if (value !== undefined && value !== null && key !== 'isPrimary') {
                        cleanedContact[key] = value;
                    }
                });

                cleanedContact.isPrimary = rawContact.isPrimary;
                formatted = {
                    businessName: getCleanedBusinessName(item[mappers[businessName].key]),
                    abn: getFormattedABN(item[mappers[abn].key]),
                    streetAddress: getCleanedStreetAddress(item[mappers[streetAddress].key]),
                    suburb: getCleanedSuburb(item[mappers[suburb].key]),
                    state: getCleanedState(item[mappers[state].key]),
                    postcode: item[mappers[postcode].key] === undefined ? "" : trimAndRemoveWhiteSpaces(String(item[mappers[postcode].key])),
                    country: getCleanedCountry(item[mappers[country].key]),
                    phoneNumber: getFormattedPhoneNumber(item[mappers[phoneNumber].key]),
                    email: trimAndRemoveWhiteSpaces(item[mappers[email].key]),
                    website: getFormattedWebsite(item[mappers[website].key]),
                    paymentTerms: Number(item[mappers[paymentTerms].key]),
                    contacts: [cleanedContact],
                };
                // Check for duplicates against existing vendors
                const vendorKey = `${formatted.businessName}-${formatted.abn || ''}`;
                if (existingVendorsMap.has(vendorKey) || (formatted.abn && existingAbn.has(formatted.abn))) {
                    item['error'] = [{
                        "instancePath": "/",
                        "schemaPath": "#/properties/abn",
                        "keyword": "errorMessage",
                        "params": {
                            "errors": [{
                                "instancePath": "/",
                                "schemaPath": "#/properties/abn/uniqueItems",
                                "keyword": "uniqueItems",
                                "params": { "i": 0, "j": 1 },
                                "message": "must have unique ABN-Business Name combinations",
                                "emUsed": true
                            }]
                        },
                        "message": "Vendor with this ABN already exists in the system"
                    }];
                    item['valid'] = false;
                    count.invalid++;
                } else {
                    // Group contacts for the validation of primary contacts
                    if (!vendorMap.has(vendorKey)) vendorMap.set(vendorKey, []);
                    vendorMap.get(vendorKey).push(formatted.contacts[0]);

                    valid = validate(formatted);
                    if (!valid) {
                        count.invalid++;
                        item['error'] = validate.errors;
                        item['valid'] = false;
                    } else {
                        if (isSubmit) _temp.push({
                            ...formatted,
                            phoneNumber: item[mappers[phoneNumber].key] ? String(item[mappers[phoneNumber].key]) : item[mappers[phoneNumber].key], // Keep original format
                            contacts: [
                                {
                                    ...formatted.contacts[0],
                                    phoneNumber: item[mappers[contactPhoneNumber].key] ? String(item[mappers[contactPhoneNumber].key]) : item[mappers[contactPhoneNumber].key] // Keep original format
                                }
                            ]
                        });
                        count.valid++;
                        item['valid'] = true;
                    }
                }
                return item;
            });

            // Check for multiple primary contacts
            vendorMap.forEach((contacts, vendorKey) => {
                const primaryContacts = contacts.filter(contact => contact.isPrimary);
                if (primaryContacts.length > 1) {
                    _data.forEach(item => {
                        if (`${item[mappers[businessName].key]}-${item[mappers[abn].key] || ''}` === vendorKey) {
                            item['error'] = [{
                                "instancePath": "/contacts",
                                "schemaPath": "#/properties/contacts/items/properties/isPrimary",
                                "keyword": "errorMessage",
                                "params": {
                                    "errors": [{
                                        "instancePath": "/contacts",
                                        "schemaPath": "#/properties/contacts/items/properties/isPrimary/maxItems",
                                        "keyword": "maxItems",
                                        "params": { "limit": 1 },
                                        "message": "must NOT have more than 1 primary contacts",
                                        "emUsed": true
                                    }]
                                },
                                "message": "Only one primary contact is allowed per Vendor"
                            }];
                            if (item['valid']) {
                                count.valid--;
                                count.invalid++;
                                item['valid'] = false;
                            }
                        }
                    });
                }
            });

            // Check for duplicates within the imported data itself
            const duplicates = _(data).groupBy(item => item[mappers[abn].key]).filter((group) => group.length > 1).value();

            duplicates.forEach((group) => {
                const businessNames = new Set();

                group.forEach((item) => {
                    if (!businessNames.has(item[mappers[businessName].key])) businessNames.add(item[mappers[businessName].key]);
                    if (businessNames.has(item[mappers[businessName].key])) {
                        _data.forEach((dataItem) => {
                            if (dataItem[mappers[abn].key] && dataItem[mappers[abn].key] === item[mappers[abn].key] &&
                                dataItem[mappers[businessName].key] !== item[mappers[businessName].key]) {

                                // Construct the error object *once* and store it in a variable
                                const error = {
                                    instancePath: "/",
                                    schemaPath: "#/properties/abn",
                                    keyword: "errorMessage",
                                    params: {
                                        errors: [{
                                            instancePath: "/",
                                            schemaPath: "#/properties/abn/uniqueItems",
                                            keyword: "uniqueItems",
                                            params: { i: 0, j: 1 },
                                            message: "must have unique ABN-Business Name combinations",
                                            emUsed: true
                                        }]
                                    },
                                    message: "Duplicate record found for ABN with different Business Name"
                                };

                                dataItem.error = dataItem.error || []; // Initialize if not present
                                const msgPresent = dataItem.error.some(e => e.message.includes(error.message));
                                if (!msgPresent) dataItem.error.push(error);

                                if (dataItem.valid) {
                                    count.valid--;
                                    count.invalid++;
                                    dataItem.valid = false;
                                }
                            }
                        });
                    }
                });
            });

            if (!_.isEqual(status, count)) setStatus(count);
            if (!isSubmit) setData(_data);

            return { count, items: _temp, data: _data };
        } catch (error) {
            console.error('Validation error:', error);
            dispatch(warningSnackBar('An error occurred during validation. Please check the data and try again.'));
            return { count, items: [], data: [] };
        }
    };

    const importData = async () => {
        let { count, items } = validateData(true);
        if (count.total == count.valid && count.total > 0) await submitHandler(items);
    };

    const submitHandler = async (items) => {
        try {
            // Group contacts by supplier
            let vendorMap = new Map();
            items.forEach(item => {
                const vendorKey = `${item.businessName}-${item.abn}`;
                if (!vendorMap.has(vendorKey)) vendorMap.set(vendorKey, { ...item, contacts: [] });
                vendorMap.get(vendorKey).contacts.push(...item.contacts);
            });

            const vendorList = Array.from(vendorMap.values()).map(vendor => ({
                ...vendor,
                type: vendorType
            }));

            if (vendorType) {
                await dispatch(addBulkVendor({ vendorList })).then(res => cancelHandler()).catch(e => console.error(e));
            }
            else dispatch(warningSnackBar('Invalid Path'))
        } catch (error) {
            console.error('Error in import process:', error);
        }
    }

    const ignoreAndImportData = async () => {
        let { count, items, data } = validateData(true);
        const validItems = (idx(data, (_) => _) || []).map(item => {
            if (item.valid) {
                return items.find(i => (i.abn || '') === (trimAndRemoveWhiteSpaces(item[abn]) || '').toString() && i.businessName === item[businessName]);
            }
        }).filter(item => item);
        if (count.valid > 0) await submitHandler(validItems);
    };

    const downloadEdit = () => {
        // Call validateData to get processed data
        let { data } = validateData(false);

        let _temp = { invalid: [], valid: [] };
        data.forEach(item => {
            let formatted = {
                ...item,
            };

            if (item.valid) {
                _temp.valid.push(formatted);
            } else {
                formatted.error = item.error.map(e => e.message).filter((message) => message).join(', \n');
                _temp.invalid.push(formatted);
            }
        });

        // Combine all data for export
        let allData = [..._temp.invalid, ..._temp.valid];

        let filename = file.name.split('.');
        filename.pop();
        filename += ' - Validated.xlsx';

        var ws = XLSX.utils.json_to_sheet(allData);
        var wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
        XLSX.writeFile(wb, filename);
    };

    const updateMappers = (key, value) => {
        setMappers((prev) => {
            const updatedMappers = {
                ...prev,
                [key]: {
                    ...prev[key],
                    key: value,
                },
            };
            const updatedData = data.map((item) => {
                return ({
                    ...item,
                    [updatedMappers[key].key]: item[updatedMappers[key].key],
                });
            });
            setData(updatedData);
            return updatedMappers;
        });
    };

    const columns = [
        { title: mappers[businessName].label, field: mappers[businessName].key, filtering: false, "data-cy": 'business-name' },
        { title: mappers[abn].label, field: mappers[abn].key, filtering: false, "data-cy": 'abn' },
        { title: mappers[streetAddress].label, field: mappers[streetAddress].key, filtering: false, "data-cy": 'street-address' },
        { title: mappers[suburb].label, field: mappers[suburb].key, filtering: false, "data-cy": 'suburb' },
        { title: mappers[state].label, field: mappers[state].key, filtering: false, "data-cy": 'state' },
        { title: mappers[postcode].label, field: mappers[postcode].key, filtering: false, "data-cy": 'postcode' },
        { title: mappers[country].label, field: mappers[country].key, filtering: false, "data-cy": 'country' },
        { title: mappers[phoneNumber].label, field: mappers[phoneNumber].key, filtering: false, "data-cy": 'phone-number' },
        { title: mappers[email].label, field: mappers[email].key, filtering: false, "data-cy": 'email' },
        { title: mappers[website].label, field: mappers[website].key, filtering: false, "data-cy": 'website' },
        { title: mappers[paymentTerms].label, field: mappers[paymentTerms].key, filtering: false, "data-cy": 'payment-terms' },
        { title: mappers[contactGivenName].label, field: mappers[contactGivenName].key, filtering: false, "data-cy": 'contact-given-name' },
        { title: mappers[contactLastName].label, field: mappers[contactLastName].key, filtering: false, "data-cy": 'contact-last-name' },
        { title: mappers[contactEmail].label, field: mappers[contactEmail].key, filtering: false, "data-cy": 'contact-email' },
        { title: mappers[contactPhoneNumber].label, field: mappers[contactPhoneNumber].key, filtering: false, "data-cy": 'contact-phone-number' },
        { title: mappers[contactJobRole].label, field: mappers[contactJobRole].key, filtering: false, "data-cy": 'contact-job-role' },
        {
            title: mappers[setPrimaryContact].label, field: mappers[setPrimaryContact].key, filtering: false, "data-cy": 'set-primary-contact',
            render: (data) => {
                const primaryContacts = data[mappers[setPrimaryContact].key] ? ['primary', 'yes', '1', 'true', true].includes(data[mappers[setPrimaryContact].key].toString().toLowerCase()) : '';
                return (primaryContacts ? 'Yes' : 'No')
            }
        },
        {
            title: 'Valid',
            field: 'valid',
            filtering: false,
            "data-cy": 'valid',
            class: 'total-width text-right',
            render: (data) => (data['valid'] ? <CheckIcon /> : <p>{typeof data['error'] === 'string' ? '' : data['error']?.map(e => e.message).filter((message) => message).join(', \n')}</p>),
        },
    ];

    const isEmptyRow = (data) => {
        if (!Array.isArray(data) || data.length === 0) return true;
        if (data.length === 1) {
            const row = data[0];
            for (const key in row) {
                if (row[key] !== "") return false;
            }
            return true;
        }
        return false;
    }

    return (
        <div className="purchase-order-block">
            <div className="row">
                <div className="w-auto">
                    <h2 className="content-heading text-uppercase">Import {vendorTypeName}</h2>
                    <div className="col-12 breadcrumb-block p-0">
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={cancelHandler} data-cy={`breadcrumb-${vendorTypeName.toLowerCase()}`}>{vendorTypeName}</Breadcrumb.Item>
                            <Breadcrumb.Item active>Import {vendorTypeName}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </div>
                <div className="w-auto ms-auto my-auto">
                    <div className="d-flex gap-3">
                        <button className="secondarybtn btn-ch btn btn-primary" onClick={downloadTemplate}>
                            Download Template
                        </button>
                        {data.length > 0 && !isEmptyRow(data) && (
                            <button className="secondarybtn secondarybtn-outline btn-ch btn btn-primary" data-cy="validate-bulk-import" onClick={() => validateData(false)}>
                                Validate
                            </button>
                        )}
                        {Number(status?.valid) > 0 && Number(status?.total) !== Number(status?.valid) && (
                            <button className="secondarybtn secondarybtn-outline btn-ch btn btn-primary" data-cy="ignore-and-import" onClick={ignoreAndImportData}>
                                Ignore and Import
                            </button>
                        )}
                        {Number(status?.total) == Number(status?.valid) && status?.total > 0 && (
                            <button className="secondarybtn secondarybtn-outline btn-ch btn btn-primary" data-cy="import-bulk-import" onClick={importData}>
                                Import
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="row mt-2 mb-1">
                <FileUploader
                    data-cy={`import-${vendorTypeName}`}
                    title={`Upload ${vendorTypeName} List`}
                    files={file ? [file] : []}
                    fileHandler={(files) => {
                        setFile(files[0]);
                        parseXlsx(files[0]);
                    }}
                    acceptedFileTypes={allowedExcelTypes}
                />
            </div>
            {data.length > 0 && !isEmptyRow(data) && (
                <>
                    <div className="row mt-2 mb-1">
                        <p className="m-0 text-decoration-underline text-left fw-medium">Fields Mapping</p>
                        {Object.entries(mappers).map((item) => {
                            let key = item[0], value = item[1];
                            return (
                                <div key={key} className="col-lg-4 col-md-6">
                                    <SelectInput
                                        placeholder={`Select ${value.label} Field`}
                                        label={value.label}
                                        items={value.items}
                                        value={value.key}
                                        onChange={(e) => updateMappers(key, e.target.value)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                        <span className="align-content-center text-decoration-underline text-left fw-medium">Data</span>
                        <div className="d-flex gap-3">
                            <span className="align-content-center text-primary" data-cy="total-count">Total : {data?.length}</span>
                            <span className="align-content-center text-success" data-cy="valid-count">Valid : {status?.valid}</span>
                            <span className="align-content-center text-danger" data-cy="invalid-count">Invalid : {status?.invalid}</span>
                        </div>
                        {Number(status?.total) > Number(status?.valid) && (
                            <button className="secondarybtn secondarybtn-outline btn-ch btn btn-primary" onClick={downloadEdit}>
                                Download & Edit
                            </button>
                        )}
                    </div>
                </>
            )}
            <CustomTable
                toolbar={false}
                enableSearch={false}
                columns={columns}
                data={isEmptyRow(data) ? [] : data}
                isEdit={false}
                fileName={vendorTypeName}
            />
        </div>
    );
}

export default ImportVendors;
